import { SuggestionKind } from '@common/CodeSuggestion';
import {
  ATTACHMENT_FOLDER_NAME,
  PATH_ROOTS,
  PIPELINE_DOCUMENT_TEMPLATE_FOLDER_NAME,
  PIPELINE_DOCUMENTS_FOLDER_NAME
} from '@common/constants';
import { ProcessKind, SqlProcessKind } from '@common/graph/types';
import { useSelector } from '@common/performance/useSelector';
import { type OpenEntityTabsState, SqlBrowserSettingsType } from '@common/preferences/localPreference';
import { isSqlReservedKeyword } from '@common/SQL/utils';
import { CommonGraph, GenericGraphProcessType } from '@common/types';
import { Entity } from '@common/types/Entity';
import { ViewType } from '@LSP/types';
import { useProviderTypes } from '@pages/Sql/EntityInfo/useProviderTypes';
import { Text, theme, Ellipsis } from '@prophecy/ui-v3';
import { NodeData } from '@prophecy/ui-v3/Graph/types';
import {
  LanguageToExtension,
  MonacoPosition,
  PrepareInsertTextOptions,
  PrepareSuggestionOptions,
  SpecFunctionType,
  Suggestion
} from '@prophecy/ui/Editor/types';
import { FROZEN_EMPTY_OBJECT } from '@prophecy/utils/constants';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { isCaseInsensitiveEqual, removeAllWhitespace } from '@prophecy/utils/string';
import {
  validateAlphaNumericAndUnderscore,
  validateFirstAlphaNumericChar,
  validateVisualGroupName
} from '@prophecy/utils/validation';
import { isEqual, last } from 'lodash-es';
import memoizeOne from 'memoize-one';
import { languages } from 'monaco-editor';
import { nanoid } from 'nanoid';
import { useCallback } from 'react';
import { Node } from 'reactflow';

import { BackEndCodeLanguage } from '../../graphqlTypes/enums';
import { getGraphProperty, getProcessById } from '../../Parser/bindings';
import { Port } from '../../redux/portTypes';
import {
  SQL_CURRENT_APP_PATH,
  SQL_CURRENT_GENERIC_DATA_TEST_PATH,
  SQL_CURRENT_MODAL_GRAPH_PATH,
  SQL_CURRENT_PIPELINE_GRAPH_PATH,
  SQL_CURRENT_SINGULAR_DATA_TEST_GRAPH_PATH,
  SQL_CURRENT_SNAPSHOT_GRAPH_PATH,
  SQL_DATASETS_PATH,
  SQL_FILESYSTEM_PATH,
  SQL_PIPELINES_PATH,
  SQL_STORED_PROCEDURES_PATH
} from './bindings';
import { DEFAULT_PROVIDER_SCHEMA_TYPE } from './schema/constants';
import {
  CurrentEntity,
  CurrentFile,
  Dataset,
  EffectRootGraph,
  EntityType,
  File,
  FileMacro,
  FileSystemNode,
  FileSystemNodeType,
  FileTypeEnum,
  Folder,
  GemEntityType,
  GenericTestEntityType,
  GraphEntity,
  JobEntityType,
  JobFolder,
  MacroEntityType,
  MacroSpecFile,
  PipelineModelNodeProperties,
  ProjectEntitiesType,
  SourceEntityType,
  SqlEntity,
  SqlEntityGraph,
  SqlGraph,
  SQLMetaInfo,
  SqlState
} from './types';
import {
  QUERY_PARAM_ASSOCIATE_ENTITY,
  QUERY_PARAM_ENTITY_ID,
  QUERY_PARAM_ENTITY_NAME,
  QUERY_PARAM_ENTITY_TYPE,
  QUERY_PARAM_FROM_PIPELINE,
  QUERY_PARAM_PACKAGE,
  useCurrentEntity
} from './useCurrentEntity';

const PROPHECY_TEMP_ENTITY_TAG = 'prophecy_temp';
const PROPHECY_INTERNAL_MACROS = ['generate_schema_name'];
const PROPHECY_INTERNAL_MACROS_CONTENT = [
  removeAllWhitespace(
    '{% set default_schema = target.schema %}\n \n{% if custom_schema_name is none %}\n  {{default_schema}}\n{% else %}\n  {{custom_schema_name | trim}}\n{% endif %} \n'
  )
];

export const SEARCH_PARAM_REDIRECT_ENTITY = 'redirectEntity';
export const SEARCH_PARAM_REDIRECT_NAME = 'redirectName';
export const SEARCH_PARAM_REDIRECT_VIEW_TYPE = 'redirectViewType';
export const SEARCH_PARAM_PROCESS_ID = 'processId';
export const CANVAS_CONTAINER_ID = 'canvascontainerId';

let fileSystemCache = new Map<string, SQLMetaInfo['fileSystem']>();

const createFileSystemObject = memoizeOne((filesystem: SQLMetaInfo['fileSystem']): SQLMetaInfo['fileSystem'] => {
  return filesystem
    ? { ...filesystem, type: FileSystemNodeType.Folder }
    : ({
        children: {},
        name: '',
        type: FileSystemNodeType.Folder,
        id: nanoid()
        //TODO: remove typecast here
      } as unknown as SQLMetaInfo['fileSystem']);
});
export function fileSystemSelector(graph: SqlGraph): SQLMetaInfo['fileSystem'] {
  const fs = getGraphProperty(graph, SQL_FILESYSTEM_PATH, PATH_ROOTS.SQL);
  return createFileSystemObject(fs);
}
export function getFileSystem(state: SqlState, fromMasterGraph?: boolean): SQLMetaInfo['fileSystem'] {
  const graph = fromMasterGraph ? state.$graph : state.currentGraph;
  const projectId = state.metadata?.project?.id;

  const cacheKey = `${projectId}-${fromMasterGraph ? 'master' : 'current'}`;

  const fileSystemFromCache = fileSystemCache.get(cacheKey);
  // if a component is opened stop listening for file system changes, and return from the cache
  if (fileSystemFromCache && state.currentComponentId) {
    return fileSystemFromCache;
  }

  const fileSystem = fileSystemSelector(graph as SqlGraph);
  if (projectId) {
    fileSystemCache.set(cacheKey, fileSystem);
  }

  return fileSystem;
}

function getDatasetColumns(dataset: Dataset) {
  if (dataset.kind === Entity.Table || dataset.kind === Entity.View) {
    return dataset.tableProperties?.columns;
  }
}

// AI sometimes generates columns which have no data type; we only create new refs when we need to add the default.
const normalizeDatasetsColumnDataTypes = memoizeOne((datasets: SQLMetaInfo['datasets']): SQLMetaInfo['datasets'] => {
  if (!datasets) return datasets;

  const needsNormalize = Object.values(datasets).some((dataset) => {
    return Boolean(getDatasetColumns(dataset)?.some((col) => !col.dataType));
  });

  if (!needsNormalize) return datasets;

  const updatedDatasets: SQLMetaInfo['datasets'] = {};
  for (const datasetId in datasets) {
    const dataset = datasets[datasetId];
    const columns = getDatasetColumns(dataset);
    if (columns && (dataset.kind === Entity.Table || dataset.kind === Entity.View)) {
      updatedDatasets[datasetId] = {
        ...dataset,
        tableProperties: {
          ...dataset.tableProperties,
          columns: columns.map((column) => ({
            ...column,
            dataType: column.dataType ?? { type: DEFAULT_PROVIDER_SCHEMA_TYPE }
          }))
        }
      };
    } else {
      updatedDatasets[datasetId] = dataset;
    }
  }
  return updatedDatasets;
});

export function datasetsSelector(state: SqlState): SQLMetaInfo['datasets'] {
  const datasets = getGraphProperty(
    state.currentGraph as SqlGraph,
    SQL_DATASETS_PATH,
    PATH_ROOTS.SQL
  ) as SQLMetaInfo['datasets'];

  return normalizeDatasetsColumnDataTypes(datasets) ?? (FROZEN_EMPTY_OBJECT as SQLMetaInfo['datasets']);
}

const createStoredProceduresObject = memoizeOne(
  (storedProcedures: SQLMetaInfo['storedProcedures']): SQLMetaInfo['storedProcedures'] => {
    return storedProcedures ?? {};
  }
);

export function getStoredProcedures(state: SqlState): SQLMetaInfo['storedProcedures'] {
  const storedProcedures = getGraphProperty(state.currentGraph as SqlGraph, SQL_STORED_PROCEDURES_PATH, PATH_ROOTS.SQL);
  return createStoredProceduresObject(storedProcedures);
}

export function getPipelines(state: SqlState): SQLMetaInfo['pipelines'] {
  const pipelines = getGraphProperty(state.currentGraph as SqlGraph, SQL_PIPELINES_PATH, PATH_ROOTS.SQL);
  return pipelines ?? (FROZEN_EMPTY_OBJECT as SQLMetaInfo['pipelines']);
}

export function findEntityFilePath({
  folder,
  entity,
  name,
  id,
  isSnapshotMerge,
  path,
  isSpecFile,
  isCodeView
}: {
  folder: Folder;
  entity: SqlEntity;
  name?: string;
  id?: string;
  isSnapshotMerge?: boolean;
  path: string;
  filePath?: string;
  isSpecFile?: boolean;
  isCodeView?: boolean;
}): string | undefined {
  const children = Object.entries(folder.children);
  for (const [childId, child] of children) {
    const childPath = `${path}.children.${childId}`;

    if (child.type === FileSystemNodeType.Folder) {
      const result = findEntityFilePath({
        folder: child,
        entity,
        name,
        isSnapshotMerge,
        id,
        path: childPath,
        isSpecFile
      });
      if (result) {
        return result;
      }
    } else if (
      child.type === FileSystemNodeType.Job &&
      isCaseInsensitiveEqual(child.type, entity) &&
      child.jobId === id
    ) {
      return childPath;
    } else if (
      (child.type === FileTypeEnum.Model || child.type === FileTypeEnum.Snapshot) &&
      (entity === Entity.Model || entity === Entity.Snapshot) &&
      child.name === name
    ) {
      return childPath;
    } else if (
      (child.type === FileTypeEnum.Seed ||
        child.type === FileTypeEnum.Pipeline ||
        child.type === FileTypeEnum.SingularDataTest) &&
      isCaseInsensitiveEqual(child.type, entity) &&
      child.name === name
    ) {
      return childPath;
    } else if (isSpecFile && child.type === FileTypeEnum.MacroSpecFile && (child as MacroSpecFile).macroName === name) {
      return childPath;
    } else if (
      !isSpecFile &&
      child.type === FileTypeEnum.Macro &&
      isCaseInsensitiveEqual(child.type, entity) &&
      child.macros?.some((macro) => macro.name === name)
    ) {
      return childPath;
    } else if (
      child.type === FileTypeEnum.GenericDataTest &&
      isCaseInsensitiveEqual(child.type, entity) &&
      child.tests?.some((test) => test.name === name)
    ) {
      return childPath;
    } else if (
      child.type === FileTypeEnum.AppPython &&
      child.type.includes(entity) &&
      (!name || child.name === name) &&
      isCodeView
    ) {
      return childPath;
    } else if (
      child.type === FileTypeEnum.App &&
      isCaseInsensitiveEqual(child.type, entity) &&
      (!name || child.name === name)
    ) {
      return childPath;
    } else if (
      child.name === name &&
      child.type === FileTypeEnum.RawFile &&
      isCaseInsensitiveEqual(entity, Entity.MarkdownDocument) &&
      [PIPELINE_DOCUMENTS_FOLDER_NAME, PIPELINE_DOCUMENT_TEMPLATE_FOLDER_NAME].includes(folder.name)
    ) {
      return childPath;
    }
  }
}

export const useIsLanguageChangeInProgress = () => {
  const isSQLSetupInProgress = useSelector<SqlState, boolean>((state) => state.isSQLSetupInProgress);
  const isPythonSetupInProgress = useSelector<SqlState, boolean>((state) => state.isPythonSetupInProgress);
  return isSQLSetupInProgress || isPythonSetupInProgress;
};

export const useIsPythonBackend = () => {
  return useSelector<SqlState, boolean>(
    (state) => state.currentGraph?.metainfo.backEndLanguage === BackEndCodeLanguage.python
  );
};

export function useCurrentEntityFilePath(
  { name, entity, id, isSnapshotMerge, isSpecFile }: CurrentEntity,
  isCodeView: boolean
) {
  const isPythonBackend = useIsPythonBackend();
  const pipelinePath = useSelector<SqlState, string | undefined>((state) => {
    if (entity !== Entity.Pipeline) return undefined;
    const metainfoPipelines = getPipelines(state);
    return metainfoPipelines?.[name || '']?.propertyPath || undefined;
  });

  const currentEntityFilePath = useSelector<SqlState, string | undefined>((state) => {
    const fileSystem = state.currentGraph?.metainfo.fileSystem;
    if (!(fileSystem && (name || id) && entity)) return undefined;
    return findEntityFilePath({
      folder: fileSystem,
      entity,
      name,
      isCodeView,
      id,
      isSnapshotMerge,
      path: SQL_FILESYSTEM_PATH,
      isSpecFile
    });
  });

  return isPythonBackend ? pipelinePath || currentEntityFilePath : currentEntityFilePath;
}

export function useMarkdownDocumentFilePath(entityName: string) {
  return useSelector<SqlState, string | undefined>((state) => {
    const fileSystem = state.currentGraph?.metainfo.fileSystem;
    if (!(fileSystem && entityName)) return undefined;
    return findEntityFilePath({
      folder: fileSystem,
      entity: Entity.MarkdownDocument,
      name: entityName.includes(LanguageToExtension.markdown)
        ? entityName
        : `${entityName}.${LanguageToExtension.markdown}`,
      id: undefined,
      isSnapshotMerge: false,
      path: SQL_FILESYSTEM_PATH,
      isSpecFile: false
    });
  });
}

export function useCurrentPipelineName() {
  const { name, entity = '' } = useCurrentEntity();
  return entity === Entity.Pipeline ? name : undefined;
}

export function useCurrentModelName() {
  const { name, entity = '' } = useCurrentEntity();
  return entity === Entity.Model || entity === Entity.Snapshot ? name : undefined;
}

export function useFileSystem() {
  return useSelector<SqlState, Folder>((state) => {
    return getFileSystem(state);
  });
}

export function getFilePath(fileSystem: SQLMetaInfo['fileSystem'], propertyPath: string) {
  const pathIds = propertyPath.replace(`${SQL_FILESYSTEM_PATH}.children.`, '').split('.children.');
  let filePath = fileSystem.name;
  let currentFileOrFolder: Folder | JobFolder = fileSystem;

  for (const pathId of pathIds) {
    const fileOrFolder: FileSystemNode = currentFileOrFolder.children[pathId];
    filePath = `${filePath}/${fileOrFolder?.name}`;

    if (
      fileOrFolder &&
      (fileOrFolder.type === FileSystemNodeType.Folder || fileOrFolder.type === FileSystemNodeType.Job)
    ) {
      currentFileOrFolder = fileOrFolder;
    }
  }

  return filePath;
}

function isEnclosed(str: string, open: string, close: string, index: number) {
  const lastIndexOfOpen = str.lastIndexOf(open, index - open.length);
  const lastIndexOfClose = str.lastIndexOf(close, index - close.length + 1);

  return lastIndexOfOpen > lastIndexOfClose;
}

function isEnclosedInJinja(code: string, position: MonacoPosition) {
  const lines = code.split('\n');
  const flattenString = lines.join('');

  const { lineNumber, column } = position;
  const charCountBeforeLine = lines.slice(0, lineNumber - 1).join('').length;

  const effectiveCharIndex = charCountBeforeLine + column - 1;

  return (
    isEnclosed(flattenString, '{{', '}}', effectiveCharIndex) ||
    isEnclosed(flattenString, '{%', '%}', effectiveCharIndex) ||
    isEnclosed(flattenString, '{#', '#}', effectiveCharIndex)
  );
}

const getSuggestionLabel = (suggestion: Suggestion) => {
  if (typeof suggestion.label === 'string') {
    return suggestion.label;
  } else {
    return (suggestion.label as languages.CompletionItemLabel).label;
  }
};

export function prepareSuggestions({ suggestions, model, position, language }: PrepareSuggestionOptions) {
  // for non sql language we don't need to modify suggestions
  if (language !== BackEndCodeLanguage.sql) {
    return suggestions;
  }

  // for sql type check if user is typing inside jinja template, if not wrap the suggestion into it
  const code = model.getValue();

  // if already enclosed in jinja template, don't wrap it
  if (isEnclosedInJinja(code, position)) {
    // if its column suggestion, column name s need to be wrapped inside quoute if it is enclosed inside jinja template
    // as the code is now on python relm
    return suggestions.map((suggestion) => {
      if (suggestion.suggestionKind === SuggestionKind.column) {
        return {
          ...suggestion,
          insertText: `'${suggestion.insertText}'`
        };
      }
      return suggestion;
    });
  }

  const suggestionsMap: Record<string, Suggestion> = {};

  suggestions.forEach((suggestion) => {
    let insertText = suggestion.insertText;
    if (
      suggestion.suggestionKind === SuggestionKind.configuration ||
      suggestion.suggestionKind === SuggestionKind.function
    ) {
      insertText = `{{ ${suggestion.insertText || suggestion.label} }}`;
    }

    const prevSuggestion = suggestionsMap[insertText];

    if (!prevSuggestion) {
      suggestionsMap[insertText] = { ...suggestion, insertText };
      return;
    }

    const prevLabel = getSuggestionLabel(prevSuggestion);
    const currentLabel = getSuggestionLabel(suggestion);
    if (currentLabel.length > prevLabel.length) {
      suggestionsMap[insertText] = { ...suggestion, insertText };
    }
  });

  return Object.values(suggestionsMap);
}

export function prepareInsertText({ insertText, code, position, spec }: PrepareInsertTextOptions) {
  if (spec?.functionType === SpecFunctionType.macro && !isEnclosedInJinja(code, position)) {
    return `{{ ${insertText} }}`;
  }
  return insertText;
}
export function findFile(
  fileSystem: SQLMetaInfo['fileSystem'],
  matcher: (node: FileSystemNode) => boolean
): { file: FileSystemNode | undefined; path: string | undefined } {
  function searchInFolder(
    folder: Folder,
    currentPath: string
  ): { file: FileSystemNode | undefined; path: string | undefined } {
    for (const [childId, child] of Object.entries(folder.children)) {
      const childPath = `${currentPath}.children.${childId}`;
      if (matcher(child)) {
        return { file: child, path: childPath };
      }
      if (child.type === FileSystemNodeType.Folder) {
        const found = searchInFolder(child, childPath);
        if (found.file) {
          return found;
        }
      }
    }
    return { file: undefined, path: undefined };
  }

  return searchInFolder(fileSystem, '');
}
export function findRelativeFileOrFolder(
  fileSystem: SQLMetaInfo['fileSystem'],
  path: string
): File | Folder | undefined {
  let currentFolder;
  const matchingPath = last(path.split('.')); // get the last part of the path which will be matching key for the file.

  function findFolder(folder: Folder) {
    if (folder.children) {
      Object.keys(folder.children).forEach((key) => {
        if (key === matchingPath) {
          currentFolder = folder.children[key];
        } else if (folder.children[key].type === 'Folder') {
          const tempFolder = folder.children[key] as Folder;
          tempFolder?.children && findFolder(tempFolder);
        }
      });
    }
  }

  findFolder(fileSystem);
  return currentFolder;
}

export function getTempModelsInFolder(folder: Folder) {
  const tempModels: Record<string, boolean> = {};
  Object.entries(folder.children).forEach(([_, child]) => {
    const modelConfigs = (child as File).folderConfiguration?.models;
    if (modelConfigs) {
      modelConfigs.forEach((modelConfig) => {
        if (modelConfig.tags?.includes(PROPHECY_TEMP_ENTITY_TAG)) {
          tempModels[modelConfig.name] = true;
        }
      });
    }
    const snapshotConfigs = (child as File).folderConfiguration?.snapshots;
    if (snapshotConfigs) {
      snapshotConfigs.forEach((snapshotConfig) => {
        if (snapshotConfig.tags?.includes(PROPHECY_TEMP_ENTITY_TAG)) {
          tempModels[snapshotConfig.name] = true;
        }
      });
    }
  });
  return tempModels;
}

export type ExtractEntitiesFromFolder = {
  pipelines: EntityType[];
  models: EntityType[];
  seeds: EntityType[];
  genericDataTests: GenericTestEntityType[];
  gems: GemEntityType[];
  sources: SourceEntityType[];
  macros: MacroEntityType[];
  singularDataTests: EntityType[];
  jobs: JobEntityType[];
  documents: EntityType[];
  templates: EntityType[];
  apps: EntityType[];
  storedProcedures: EntityType[];
  attachments: EntityType[];
};
function extractEntitiesFromFolder({
  fileSystem,
  path,
  filePath,
  sqlBrowserSettings
}: {
  fileSystem: SQLMetaInfo['fileSystem'];
  path: string;
  filePath: string;
  sqlBrowserSettings?: SqlBrowserSettingsType;
}): ExtractEntitiesFromFolder {
  const children = Object.entries(fileSystem.children);
  const pipelines: EntityType[] = [];
  const models: EntityType[] = [];
  const seeds: EntityType[] = [];
  const macros: MacroEntityType[] = [];
  const gems: GemEntityType[] = [];
  const sources: SourceEntityType[] = [];
  const jobs: JobEntityType[] = [];
  const singularDataTests: EntityType[] = [];
  const genericDataTests: GenericTestEntityType[] = [];
  const markdownDocuments: EntityType[] = [];
  const markdownTemplates: EntityType[] = [];
  const attachments: EntityType[] = [];
  const apps: EntityType[] = [];
  const storedProcedures: EntityType[] = [];
  const tempModels = getTempModelsInFolder(fileSystem);

  for (const [childId, child] of children) {
    const childPath = `${path}.children.${childId}`;
    const name = child.name || '';
    const currentFilePath = filePath ? filePath + '/' + name : name;
    if (child.type === FileSystemNodeType.Folder) {
      const {
        pipelines: childPipelines,
        models: childModels,
        seeds: childSeeds,
        macros: childMacros,
        sources: childSources,
        singularDataTests: childSingularDataTests,
        jobs: childJobs,
        gems: childGems,
        genericDataTests: childGenericTests,
        documents: childDocuments,
        templates: childTemplates,
        apps: childApps,
        storedProcedures: childStoredProcedures,
        attachments: childAttachments
      } = extractEntitiesFromFolder({
        fileSystem: child,
        path: childPath,
        filePath: currentFilePath,
        sqlBrowserSettings
      });
      pipelines.push(...childPipelines);
      models.push(...childModels);
      seeds.push(...childSeeds);
      macros.push(...childMacros);
      sources.push(...childSources);
      jobs.push(...childJobs);
      singularDataTests.push(...childSingularDataTests);
      gems.push(...childGems);
      genericDataTests.push(...childGenericTests);
      markdownDocuments.push(...childDocuments);
      markdownTemplates.push(...childTemplates);
      apps.push(...childApps);
      storedProcedures.push(...childStoredProcedures);
      attachments.push(...childAttachments);
    } else if (child.type === FileTypeEnum.Pipeline) {
      pipelines.push({
        path: childPath,
        name: child.name,
        filePath: currentFilePath,
        entity: child.type as FileTypeEnum
      });
    } else if (child.type === FileTypeEnum.StoredProcedure) {
      storedProcedures.push({
        path: childPath,
        name: child.name,
        filePath: currentFilePath,
        entity: child.type as FileTypeEnum
      });
    } else if (child.type === FileTypeEnum.Model || child.type === FileTypeEnum.Snapshot) {
      if (sqlBrowserSettings === undefined || sqlBrowserSettings.showModels) {
        models.push({
          path: childPath,
          name: child.name,
          filePath: currentFilePath,
          entity: child.type as FileTypeEnum,
          isTempModel: tempModels[child.name]
        });
      }
    } else if (child.type === FileTypeEnum.Seed) {
      seeds.push({ path: childPath, name: child.name, filePath: currentFilePath, content: child.content });
    } else if (child.type === FileTypeEnum.SingularDataTest) {
      singularDataTests.push({ path: childPath, name: child.name, filePath: currentFilePath });
    } else if (child.type === FileTypeEnum.App) {
      apps.push({ path: childPath, name: child.name, filePath: currentFilePath, content: child.content });
    } else if (child.type === FileTypeEnum.MacroSpecFile) {
      const _child = child as unknown as GemEntityType;
      gems.push({
        path: childPath,
        name: _child.name,
        filePath: currentFilePath,
        type: FileTypeEnum.MacroSpecFile,
        macroName: _child.macroName,
        project: _child.project,
        category: _child.category
      });
    } else if ('tests' in child) {
      const currentGenericTestCount = genericDataTests.length;
      genericDataTests.push(
        ...child.tests!.map((test, index) => ({
          ...test,
          path: childPath,
          filePath: currentFilePath + '/' + test.name,
          name: test.name,
          index: currentGenericTestCount + index
        }))
      );
    } else if ('macros' in child) {
      const currentMacroCount = macros.length;
      // Filter out Prophecy's internal generated macros by checking both the macro name and definition
      const isExternalMacro = (macro: FileMacro) =>
        !PROPHECY_INTERNAL_MACROS.includes(macro.name) &&
        !PROPHECY_INTERNAL_MACROS_CONTENT.includes(removeAllWhitespace(macro.definition));
      const externalMacros = (child.macros || []).filter(isExternalMacro);

      macros.push(
        ...externalMacros.map((macro, index) => ({
          path: childPath,
          filePath: currentFilePath + '/' + macro.name,
          name: macro.name,
          macroType: macro.macroType,
          macroIndex: currentMacroCount + index
        }))
      );
    } else if (child.type === 'YAML') {
      if (child.folderConfiguration?.sources?.length) {
        sources.push(
          ...(child.folderConfiguration?.sources || []).map((source, srcIdx) => ({
            path: `${childPath}`,
            srcIdx,
            filePath: currentFilePath,
            name: source.name,
            tables: source.tables
              ? source.tables.map((table, tableIdx) => ({
                  path: `${childPath}`,
                  filePath: currentFilePath,
                  name: table?.name,
                  columns: table?.columns,
                  srcIdx,
                  tableIdx
                }))
              : []
          }))
        );
      }
    } else if (child.type === FileSystemNodeType.Job) {
      const currentChild = child as unknown as JobEntityType;
      jobs.push({
        path: childPath,
        name: child.name,
        jobId: child.jobId,
        filePath: currentFilePath,
        models: currentChild.models,
        enabled: currentChild.enabled,
        projects: currentChild.projects,
        scheduleCron: currentChild.scheduleCron,
        scheduleHelp: currentChild.scheduleHelp
      });
    } else if (child.type === FileTypeEnum.RawFile && [PIPELINE_DOCUMENTS_FOLDER_NAME].includes(filePath)) {
      markdownDocuments.push({
        path: childPath,
        name: child.name,
        filePath: currentFilePath
      });
    } else if (child.type === FileTypeEnum.RawFile && [PIPELINE_DOCUMENT_TEMPLATE_FOLDER_NAME].includes(filePath)) {
      markdownTemplates.push({
        path: childPath,
        name: child.name,
        filePath: currentFilePath
      });
    } else {
      if (child.type === FileTypeEnum.RawFile && currentFilePath.startsWith(ATTACHMENT_FOLDER_NAME)) {
        attachments.push({
          path: childPath,
          name: child.name,
          filePath: currentFilePath
        });
      }
    }
  }

  return {
    pipelines,
    models,
    seeds,
    genericDataTests,
    gems,
    sources,
    macros,
    singularDataTests,
    jobs,
    documents: markdownDocuments,
    templates: markdownTemplates,
    attachments,
    apps,
    storedProcedures
  };
}

export const extractEntitiesFromFilesystem = memoizeOne(
  (fileSystem: SQLMetaInfo['fileSystem'], sqlBrowserSettings?: SqlBrowserSettingsType) => {
    return extractEntitiesFromFolder({ fileSystem, sqlBrowserSettings, filePath: '', path: '' });
  }
);

export function useProjectEntities() {
  return useSelector<SqlState, ProjectEntitiesType>((state) => {
    const fileSystem = getFileSystem(state);
    return extractEntitiesFromFilesystem(fileSystem);
  }, isEqual);
}

type ProcessWithPort = Exclude<GenericGraphProcessType<SqlGraph>, 'ports'> & {
  ports: {
    inputs: Port[];
    outputs: Port[];
  };
};

function addInitialPorts(process: GenericGraphProcessType<SqlGraph>) {
  if (!process.ports) {
    process.ports = {
      inputs: [],
      outputs: []
    };
  }
  return process as ProcessWithPort;
}

export function addPorts(graph: SqlGraph) {
  const { processes, connections } = graph;
  connections.forEach((connection) => {
    const source = addInitialPorts(processes[connection.source]);
    const target = addInitialPorts(processes[connection.target]);

    let outputPortId: string = source.ports.outputs[0]?.id;

    // add source ports
    if (!outputPortId) {
      outputPortId = nanoid();
      source.ports.outputs.push({
        id: outputPortId,
        slug: ''
      });
    }
    connection.sourcePort = outputPortId;

    // add target ports (but not for Seed components)
    if (!isCaseInsensitiveEqual(target.component, SqlProcessKind.Seed)) {
      const inputPortId = nanoid();
      target.ports.inputs.push({
        id: inputPortId,
        slug: ''
      });
      connection.targetPort = inputPortId;
    }
  });

  // add ports to leaf nodes
  Object.keys(processes).forEach((processId) => {
    const outputs = processes[processId].ports?.outputs || [];
    if (outputs.length === 0) {
      outputs.push({
        id: nanoid(),
        slug: ''
      });
    }
  });
}

export function getEntityRootGraphFromState(state: SqlState) {
  const currentGraph = state.currentGraph as SqlGraph;
  let graph: CommonGraph | undefined = undefined;
  let graphPath: string | undefined = undefined;
  // compute entity from search params
  const searchParams = new URLSearchParams(window.location.search);
  const entityFromSearchParams = searchParams.get('entity') as SqlEntity | undefined;
  if (isCaseInsensitiveEqual(entityFromSearchParams, Entity.Job)) {
    return { graph: undefined, graphPath: undefined };
  }
  if (state.graphPath === PATH_ROOTS.SQL) {
    graph = currentGraph;
    graphPath = state.graphPath;
  } else if (currentGraph?.currentPipelineGraph) {
    graph = currentGraph.currentPipelineGraph;
    graphPath = SQL_CURRENT_PIPELINE_GRAPH_PATH;
  } else if (currentGraph?.currentSnapshotGraph) {
    graph = currentGraph.currentSnapshotGraph;
    graphPath = SQL_CURRENT_MODAL_GRAPH_PATH;
  } else if (currentGraph?.currentModelGraph) {
    graph = currentGraph.currentModelGraph;
    graphPath = SQL_CURRENT_MODAL_GRAPH_PATH;
  } else if (currentGraph?.currentSingularDataTestGraph) {
    graph = currentGraph.currentSingularDataTestGraph;
    graphPath = SQL_CURRENT_SINGULAR_DATA_TEST_GRAPH_PATH;
  } else if (currentGraph?.currentGenericDataTest) {
    graph = undefined;
    graphPath = SQL_CURRENT_GENERIC_DATA_TEST_PATH;
  } else if (currentGraph?.currentApp) {
    graph = undefined;
    graphPath = SQL_CURRENT_APP_PATH;
  }

  return { graph, graphPath };
}

export function usePortProcess<G extends CommonGraph>() {
  const currentGraph = useSelector<SqlState, CommonGraph | undefined>((state) => {
    return getEntityRootGraphFromState(state as SqlState).graph;
  });

  return usePersistentCallback(
    <T,>(
      portId: string,
      processSelector: (process?: GenericGraphProcessType<G>) => T = (process) => process as unknown as T
    ) => {
      const processId = currentGraph?.connections.find(({ targetPort }) => targetPort === portId)?.source;
      return processSelector(processId ? getProcessById(currentGraph as G, processId) : undefined);
    }
  );
}

export const useGetPortLabel = () => {
  const getPortInfo = usePortProcess();
  return usePersistentCallback((port: Port) => {
    if (!port.slug) return '';

    const processName = getPortInfo(port.id, (process) => process?.metadata.label);
    if (processName) return `${port.slug} (${processName})`;
    return port.slug;
  });
};

export function getEffectiveRootGraph(rootGraph: SqlGraph): EffectRootGraph | undefined {
  // At one time there would be only one graph.
  return (
    rootGraph.currentSnapshotGraph ||
    rootGraph.currentModelGraph ||
    rootGraph.currentSingularDataTestGraph ||
    rootGraph.currentPipelineGraph
  );
}

export function isGraphEntity(entity?: SqlEntity) {
  return Boolean(entity && GraphEntity.some((entityType) => entityType === entity));
}
export function isSqlLineageGraphPath(graphPath: string) {
  return graphPath === PATH_ROOTS.SQL;
}
export function isSqlPipelineGraphPath(graphPath: string) {
  return graphPath ? graphPath.startsWith(SQL_CURRENT_PIPELINE_GRAPH_PATH) : false;
}
export function isSqlModelGraphPath(graphPath: string) {
  return graphPath ? graphPath.startsWith(SQL_CURRENT_MODAL_GRAPH_PATH) : false;
}
export function isSqlSnapshotGraphPath(graphPath: string) {
  return graphPath ? graphPath.startsWith(SQL_CURRENT_SNAPSHOT_GRAPH_PATH) : false;
}
export function isSingularDataTestGraphPath(graphPath: string) {
  return graphPath ? graphPath.startsWith(SQL_CURRENT_SINGULAR_DATA_TEST_GRAPH_PATH) : false;
}
export function isSqlSubgraph(graph?: SqlGraph) {
  return graph?.component === ProcessKind.Subgraph;
}

export function validateSqlEntity(name: string, checkReservedKeyword = true) {
  if (isSqlReservedKeyword(name) && checkReservedKeyword) {
    return 'Reserved keywords are not allowed';
  }

  return validateAlphaNumericAndUnderscore(name) || validateFirstAlphaNumericChar(name);
}

export function validateSqlGemName(name: string) {
  if (isSqlReservedKeyword(name)) {
    return 'Reserved keywords are not allowed';
  }

  return validateAlphaNumericAndUnderscore(name);
}

export function validateSqlVisualGroupName(name: string) {
  if (isSqlReservedKeyword(name)) {
    return 'Reserved keywords are not allowed';
  }

  return validateVisualGroupName(name);
}

export function checkFileSystemHasModels(fileSystem: SQLMetaInfo['fileSystem']): boolean {
  const children = Object.values(fileSystem.children);

  // if any model present as direct children return true.
  if (children.find((child) => child.type === FileTypeEnum.Model)) return true;

  // if not in direct children look for children in folder
  return !!children.find((child) => child.type === FileSystemNodeType.Folder && checkFileSystemHasModels(child));
}

export function checkFileSystemHasPipelines(fileSystem: SQLMetaInfo['fileSystem']): boolean {
  const children = Object.values(fileSystem.children);

  // if any pipeline present as direct children return true.
  if (children.find((child) => child.type === FileTypeEnum.Pipeline)) return true;

  // if not in direct children look for children in folder
  return !!children.find((child) => child.type === FileSystemNodeType.Folder && checkFileSystemHasPipelines(child));
}

export function handleDocumentParamsOnDialogClose(
  setSearchParams: (searchParams: URLSearchParams) => void,
  setViewType: (viewType: ViewType) => void
) {
  const currentSearchParams = new URLSearchParams(window.location.search);
  const redirectEntity = currentSearchParams.get(SEARCH_PARAM_REDIRECT_ENTITY);
  const redirectName = currentSearchParams.get(SEARCH_PARAM_REDIRECT_NAME);
  const redirectViewType = currentSearchParams.get(SEARCH_PARAM_REDIRECT_VIEW_TYPE);

  if (redirectEntity && redirectName) {
    currentSearchParams.set('entity', redirectEntity);
    currentSearchParams.set('name', redirectName);

    currentSearchParams.delete(SEARCH_PARAM_REDIRECT_ENTITY);
    currentSearchParams.delete(SEARCH_PARAM_REDIRECT_NAME);
  }

  if (redirectViewType) {
    setViewType(redirectViewType as ViewType);
    currentSearchParams.delete(SEARCH_PARAM_REDIRECT_VIEW_TYPE);
  }
  currentSearchParams.delete(SEARCH_PARAM_PROCESS_ID);
  // Only update if query string changed
  const newSearchString = currentSearchParams.toString();
  if (newSearchString !== window.location.search.replace(/^\?/, '')) {
    setSearchParams(currentSearchParams);
  }
}

/**
 * @type InlineModelReverseMap - A map of inline model files to their corresponding process ids and children process ids.
 */
type InlineModelReverseMap = Record<string, { id: string; children: Set<string> }>;

/**
 * @param currentPipelineGraph - The current pipeline graph.
 * @returns InlineModelReverseMap - A map of model files to their corresponding process ids and children process ids.
 */
export function getInlineModelReverseMap(currentPipelineGraph?: SqlEntityGraph): InlineModelReverseMap {
  const processesVsModelFiles = currentPipelineGraph?.metainfo?.processesVsModelFiles;
  const processes = currentPipelineGraph?.processes;
  if (!processesVsModelFiles || !processes) return FROZEN_EMPTY_OBJECT as InlineModelReverseMap;
  const result: InlineModelReverseMap = {};
  for (const [processPath, modelPath] of Object.entries(processesVsModelFiles)) {
    if (!result[modelPath]) {
      result[modelPath] = { id: '', children: new Set<string>() };
    }
    if (processPath in processes) {
      result[modelPath].children.add(processPath);
    } else {
      result[modelPath].id = processPath;
    }
  }
  return result;
}

/**
 * @param selectedNodesOnCanvas - The selected nodes on canvas.
 * @param inlineModelReverseMap - The inline model reverse map.
 * @returns The inline model formed by the selected nodes.
 */
export function getInlineModelForSelectedNodes(
  selectedNodesOnCanvas: Node<NodeData>[],
  inlineModelReverseMap: InlineModelReverseMap
) {
  for (const inlineModelPath in inlineModelReverseMap) {
    const children = inlineModelReverseMap[inlineModelPath].children;
    // checking if all selected nodes are children of the inline model
    for (let node of selectedNodesOnCanvas) {
      if (!children.has(node.id)) {
        return;
      }
    }
    // there can be other nodes selected along which are not children of the inline model
    if (children.size === selectedNodesOnCanvas.length) {
      return {
        inlineModelPath,
        inlineModelId: inlineModelReverseMap[inlineModelPath].id
      };
    }
  }
}

/**
 * @param selectedNodesOnCanvas - The selected nodes on canvas.
 * @param currentPipelineGraph - The current pipeline graph.
 * @param fs - The file system.
 * @returns FileObject of the selected model or inline model formed by the selected nodes.
 */
export function getFilePathFromSelectedNodesOnCanvas({
  selectedNodesOnCanvas,
  currentPipelineGraph,
  fs
}: {
  selectedNodesOnCanvas: Node<NodeData>[];
  currentPipelineGraph?: SqlEntityGraph;
  fs: SQLMetaInfo['fileSystem'];
}): CurrentFile | undefined {
  if (!selectedNodesOnCanvas?.length || !currentPipelineGraph) {
    return;
  }

  if (selectedNodesOnCanvas.length === 1) {
    // if only one node is selected then it should be an explicit model
    const node = selectedNodesOnCanvas[0];
    if (node.data.gemType === Entity.Model) {
      const modelName = (currentPipelineGraph.processes[node.id].properties as PipelineModelNodeProperties).modelName;
      if (modelName) {
        // name is unique, hence comparing with name
        const model = extractEntitiesFromFilesystem(fs).models.find((model) => model.name === modelName);
        if (model) {
          return {
            propertyPath: SQL_FILESYSTEM_PATH + model.path,
            type: Entity.Model
          };
        }
      }
    }
    return;
  }

  // possibility of inline mode if more than one node is selected
  const inlineModelReverseMap = getInlineModelReverseMap(currentPipelineGraph);
  const inlineModelForSelectedNodes = getInlineModelForSelectedNodes(selectedNodesOnCanvas, inlineModelReverseMap);
  if (!inlineModelForSelectedNodes) {
    return;
  }
  // '.' is not allowed in model name, but modelPath from reverse map has ${name}.${extension} format
  // so removing the format
  const modelPath = inlineModelForSelectedNodes.inlineModelPath.split('.')[0];
  const model = extractEntitiesFromFilesystem(fs).models.find((model) => model.filePath === modelPath);
  if (model) {
    return {
      propertyPath: SQL_FILESYSTEM_PATH + model.path,
      type: Entity.Model
    };
  }
}

function hasConfigStringError(string: string) {
  const isConfigString = string.trim().startsWith('{');
  const startsWithConfigStr = string.trim().startsWith('{{');
  const endsWithConfigStr = string.trim().endsWith('}}');
  let hasError = true;
  if (startsWithConfigStr && endsWithConfigStr) {
    hasError = false;
  }
  return { hasError, isConfigString };
}

const VALID_TABLE_AND_SCHEMA_NAME_REGEX = /^[a-zA-Z0-9_]*$/;
// Double-quoted identifiers preserve case in Snowflake (e.g. "myTable" stays lowercase)
const VALID_QUOTED_IDENTIFIER_REGEX = /^"[^"]+?"$/;
function isValidTableAndSchemaName(name: string, allowQuotedIdentifiers?: boolean): boolean {
  if (allowQuotedIdentifiers && VALID_QUOTED_IDENTIFIER_REGEX.test(name)) return true;
  return VALID_TABLE_AND_SCHEMA_NAME_REGEX.test(name);
}

/**
 * @param value - The value to validate.
 * @param allowQuotedIdentifiers - When true, double-quoted identifiers like "myName" are valid (Snowflake).
 * @returns { hasError: boolean, isConfigString: boolean } - Whether the value has an error and whether it is a config string.
 */
export const getSchemaAndTableValidationResult = (value: string, allowQuotedIdentifiers?: boolean) => {
  const { hasError: hasConfigError, isConfigString } = hasConfigStringError(value);
  const hasInvalidChars = !isValidTableAndSchemaName(value, allowQuotedIdentifiers);
  return { hasError: isConfigString ? hasConfigError : hasInvalidChars, isConfigString };
};

/**
 * Builds the `search` string to restore the last SQL entity from project preferences,
 * or `undefined` when no redirect should run.
 */
export function getRestoredEntitySearch({
  justExitedEntityToBaseUrl,
  isOnboardingEnabled,
  hasEntityInUrl,
  persistedOpenEntityTabs,
  locationSearch
}: {
  justExitedEntityToBaseUrl: boolean;
  isOnboardingEnabled: boolean;
  /** When the URL already specifies an entity, we do not override it from preferences. */
  hasEntityInUrl: boolean;
  persistedOpenEntityTabs: OpenEntityTabsState | undefined;
  locationSearch: string;
}): string | undefined {
  if (justExitedEntityToBaseUrl) {
    return undefined;
  }
  if (isOnboardingEnabled || hasEntityInUrl) {
    return undefined;
  }

  const activeEntity = persistedOpenEntityTabs?.activeEntity;
  if (!activeEntity?.entity) {
    return undefined;
  }

  const searchParams = new URLSearchParams(locationSearch);
  searchParams.set(QUERY_PARAM_ENTITY_TYPE, activeEntity.entity.toLowerCase());
  searchParams.delete(QUERY_PARAM_ENTITY_NAME);
  searchParams.delete(QUERY_PARAM_ENTITY_ID);
  searchParams.delete(QUERY_PARAM_ASSOCIATE_ENTITY);
  searchParams.delete(QUERY_PARAM_PACKAGE);
  searchParams.delete(QUERY_PARAM_FROM_PIPELINE);

  if (activeEntity.name) {
    searchParams.set(QUERY_PARAM_ENTITY_NAME, activeEntity.name);
  }
  if (activeEntity.id) {
    searchParams.set(QUERY_PARAM_ENTITY_ID, activeEntity.id);
  }
  if (activeEntity.associateEntity) {
    searchParams.set(QUERY_PARAM_ASSOCIATE_ENTITY, activeEntity.associateEntity);
  }
  if (activeEntity.packageName) {
    searchParams.set(QUERY_PARAM_PACKAGE, activeEntity.packageName);
  }
  if (activeEntity.fromPipeline) {
    searchParams.set(QUERY_PARAM_FROM_PIPELINE, activeEntity.fromPipeline);
  }

  return `?${searchParams.toString()}`;
}

const SCHEMA_AND_TABLE_VALIDATION_MESSAGE =
  'Invalid name format. Use only letters, numbers, and underscores, or wrap config strings in {{ }}.';

const SCHEMA_AND_TABLE_VALIDATION_MESSAGE_SNOWFLAKE =
  'Invalid name format. Use letters, numbers, underscores, double-quoted identifiers (e.g. "myName"), or config strings in {{ }}.';

export function useSchemaAndTableValidation() {
  const { isSnowFlake } = useProviderTypes();

  const validate = useCallback((value: string) => getSchemaAndTableValidationResult(value, isSnowFlake), [isSnowFlake]);

  const validationMessage = isSnowFlake
    ? SCHEMA_AND_TABLE_VALIDATION_MESSAGE_SNOWFLAKE
    : SCHEMA_AND_TABLE_VALIDATION_MESSAGE;

  return { validate, validationMessage, isSnowFlake };
}

function SearchTextLabel({
  label,
  create,
  createPrefix = 'Create new'
}: {
  label: string;
  create: boolean;
  createPrefix?: string;
}): React.ReactNode {
  return (
    <Ellipsis tooltip={true}>
      <Text level='sm' tone={theme.colors.gray500}>
        {create ? `${createPrefix} "${label}"` : label}
      </Text>
    </Ellipsis>
  );
}
export function getSearchTextLabelCreatable({
  label,
  create,
  createPrefix,
  hide
}: {
  label: string;
  create: boolean;
  createPrefix?: string;
  hide?: boolean;
}): React.ReactNode | undefined {
  return hide ? undefined : <SearchTextLabel label={label} create={create} createPrefix={createPrefix} />;
}
